import { SaveActionType } from '@common/constants';
import { CustomerInfo } from '@common/models/customer-info.model';
import { Estimate } from '@common/models/estimate.model';
import { SaveAction } from '@common/models/save-action.model';
import { replacerV3 } from '@common/utils/estimate.utils';

export interface SaveEstimateItem {
    productId: number;
    quantity: number;
}

/**
 * CommonEstimateDto includes elements common to both the POST and GET of the estimate APIs
 */
export interface CommonEstimateDto {
    code: string; // this is the guid/token
    storeId: number;
    customerEmail: string;
    customerName: string;
    customerAddress1: string;
    customerAddress2?: string;
    customerCity: string;
    customerState: string;
    customerZip: string;
    customerPhone: string;
    customerAlternatePhone: string;
    note: string;
    ticketNumber?: string;
    userToken?: string;
}

export interface SaveEstimateDto extends CommonEstimateDto {
    discountOverride: number | undefined;
    prodQteGrpID: number;
    estimateItems: SaveEstimateItem[];
    estimateImage: string;
    saveAction: SaveActionType;
    estimateData: Estimate;
    sendCustomerEmail: boolean;
    externalId?: string | null; // External ID (used by RTA initially) to associate with the estimate
}

export interface EstimateResponseMessage {
    cartStatus: boolean;
    cartMessage: string;
}

export interface SaveEstimateResponse {
    estimateNumber: string;
    code: string;
    paymentUrl: string; // this is ONLY for IME (which is going away), to _their_ checkout/payment page
    redirectUrl: string; // this is the RTA shopping cart checkout URL
    messages: EstimateResponseMessage[];
}

export interface Revision {
    estimateNumber: string;
    lastModified: string; //"2022-11-21T15:45:49.899Z",
    code: string;
}

export interface GetEstimateDto extends CommonEstimateDto {
    estimateNumber: string;
    status: string;
    paymentUrl?: string;
    quoteDate: string;
    quoteExpirationDate: string;
    revisions: Revision[];
    saveActions: SaveAction[] | null;
    estimateData?: Estimate;
}

export const makeSaveEstimateDto = ({
    discountOverride,
    customer,
    estimateData,
    estimateImage,
    lineItems,
    saveAction,
    code,
    ticketNumber,
    externalId,
    userToken,
}: {
    discountOverride?: number;
    customer: CustomerInfo;
    estimateData: Estimate;
    estimateImage: string;
    lineItems: SaveEstimateItem[];
    saveAction: SaveActionType;
    code?: string;
    ticketNumber?: string;
    externalId?: string;
    userToken?: string;
}): SaveEstimateDto => {
    return {
        discountOverride,
        userToken,
        code: code ? code : '', // must be empty string instead of null
        customerEmail: customer.email,
        estimateData: estimateData,
        estimateImage: estimateImage,
        estimateItems: lineItems,
        prodQteGrpID: estimateData.qteGrpID ?? 0,
        storeId: estimateData.storeId || 0, //TODO Fix the types, should never be zero
        customerName: customer.customerName,
        customerAddress1: customer.address1,
        customerAddress2: customer.address2,
        customerCity: customer.city,
        customerState: customer.state,
        customerZip: customer.zip,
        customerPhone: customer.phone,
        customerAlternatePhone: customer.alternatePhone,
        sendCustomerEmail: customer.sendCustomerEmail ?? true,
        saveAction: saveAction,
        ticketNumber: ticketNumber,
        externalId: externalId ?? null,
        note: estimateData.productNotes
            .map((value) => [value.productDescription.trim() + ':', value.note.trim()].join('\n'))
            .join('\n\n'),
    };
};

export const makeSaveEstimateFormData = ({
    discountOverride,
    customer,
    estimateData,
    estimateImageBase64,
    externalId,
    lineItems,
    saveAction,
    ticketNumber,
    code,
    userToken,
}: {
    customer: CustomerInfo;
    discountOverride?: number;
    estimateData: Estimate;
    estimateImageBase64: string;
    externalId?: string;
    lineItems: SaveEstimateItem[];
    saveAction: SaveActionType;
    ticketNumber?: string;
    code?: string;
    userToken?: string;
}): FormData => {
    const formData = new FormData();

    const estimateDto = makeSaveEstimateDto({
        customer,
        discountOverride,
        estimateData,
        estimateImage: '',
        externalId,
        lineItems,
        saveAction,
        ticketNumber,
        code,
        userToken,
    });

    Object.entries(estimateDto).forEach(([key, value]) => {
        // ignore estimateImage from here, it's blank anyhow
        if (key === 'estimateImage') {
            return;
        }
        if (key === 'estimateData') {
            // handle the json element (estimateData should be the only one)
            formData.append(key, JSON.stringify(value, replacerV3));
        } else if (Array.isArray(value)) {
            // All arrays should be broken out into individual rows and elements
            value.forEach((item: SaveEstimateItem, index: number) => {
                Object.entries(item).forEach(([itemKey, itemValue]) => {
                    formData.append(`${key}[${index}].${itemKey}`, String(itemValue));
                });
            });
        } else if (value !== undefined && value !== null) {
            formData.append(key, String(value));
        }
    });

    const imageBlob = new Blob([Uint8Array.from(atob(estimateImageBase64), (c) => c.charCodeAt(0))], {
        type: 'image/png',
    });

    formData.append('estimateImage', imageBlob, 'estimate.png');

    return formData;
};
export class EstimateStatus {
    imeStatus: string | null;
    imeMessage: string | null;
    status: string | null;
    paymentUrl: string | null;

    constructor(init?: Partial<EstimateStatus>) {
        this.imeStatus = init?.imeStatus ?? null;
        this.imeMessage = init?.imeMessage ?? null;
        this.status = init?.status ?? null;
        this.paymentUrl = init?.paymentUrl ?? null;
    }
}
